import { HttpErrorResponse } from "@angular/common/http";

import { FinancialInstitute } from "@core/models/financial-insitute.model";
import { Intermediary } from "@core/models/intermediary.model";
import { TableSorting, TPagination } from "@custom-types/sorting";
import { AccountManager } from "@models/account-manager.model";
import { Advisor } from "@models/advisor.model";
import { Country } from "@models/country.model";
import { Document } from "@models/document.model";
import { Email } from "@models/email.model";
import { Etf } from "@models/etf-model";
import { Insurer } from "@models/insurer.model";
import { createAction, props } from "@ngrx/store";

export const LOAD_ADVISORS = createAction(
  "[Management] Load Advisors",
  props<{
    searchTerm?: string;
    pagination?: Partial<TPagination>;
  }>()
);

export const SET_ADVISORS = createAction(
  "[Management] Set Advisors",
  props<{ advisorList: Array<Advisor> }>()
);

export const LOAD_INSURERS = createAction(
  "[Management] Load Insurers",
  props<{
    searchTerm?: string;
    pagination?: Partial<TPagination>;
  }>()
);

export const SET_INSURERS = createAction(
  "[Management] Set Insurers",
  props<{ insurerList: Array<Insurer> }>()
);

export const LOAD_ACCOUNT_MANAGERS = createAction(
  "[Management] Load Account Managers",
  props<{ pagination?: Partial<TPagination> }>()
);

export const SET_ACCOUNT_MANAGERS = createAction(
  "[Management] Set Account Managers",
  props<{ accountManagerList: Array<AccountManager> }>()
);

export const SET_MANAGEMENT_LOADING_STATUS = createAction(
  "[Management] Set Management Loading Status",
  props<{ loading: boolean }>()
);

export const LOAD_EMAILS = createAction(
  "[Management] Load Emails",
  props<{ pagination?: Partial<TPagination> }>()
);

export const LOAD_DOCUMENTS = createAction(
  "[Management] Load Documents",
  props<{ pagination?: Partial<TPagination> }>()
);

export const SET_EMAILS = createAction(
  "[Management] Set Emails",
  props<{ emailList: Array<Email> }>()
);

export const SET_DOCUMENTS = createAction(
  "[Management] Set Documents",
  props<{ documentList: Array<Document> }>()
);

export const SET_EMAILS_LOADING_STATUS = createAction(
  "[Management] Set Emails Loading Status",
  props<{ loading: boolean }>()
);

export const SET_DOCUMENTS_LOADING_STATUS = createAction(
  "[Management] Set Documents Loading Status",
  props<{ loading: boolean }>()
);

export const SET_LAST_ACTION_DATE = createAction(
  "[Management] Set Last Action Date",
  props<{ date: Date }>()
);

export const SET_MANAGEMENT_ERROR = createAction(
  "[Management] Set Management Error",
  props<{ error: HttpErrorResponse }>()
);

export const LOAD_FINANCIAL_INSTITUTIONS = createAction(
  "[Management] Load Financial Institutions",
  props<{ searchTerm?: string }>()
);

export const SET_FINANCIAL_INSTITUTIONS = createAction(
  "[Management] Set Financial Institutions",
  props<{ financialInstituteList: Array<FinancialInstitute> }>()
);

export const LOAD_INTERMEDIARIES = createAction(
  "[Management] Load Intermediaries",
  props<{
    searchTerm?: string;
    pagination?: Partial<TPagination>;
  }>()
);

export const SET_INTERMEDIARIES = createAction(
  "[Management] Set Intermediaries",
  props<{ intermediaryList: Array<Intermediary> }>()
);

export const SAVE_ADVISOR = createAction(
  "[Management] Save Advisor",
  props<{ advisor: Partial<Advisor>; id: string | undefined }>()
);

export const SAVE_INSURER = createAction(
  "[Management] Save Insurer",
  props<{ insurer: Partial<Insurer>; id: string | undefined }>()
);

export const LOAD_COUNTRIES = createAction("[Management] Load Countries");

export const SET_COUNTRIES = createAction(
  "[Management] Set Countries",
  props<{ countryList: Array<Country> }>()
);

export const SAVE_FINANCIAL_INSTITUTION = createAction(
  "[Management] Save Financial Institution",
  props<{ institution: Partial<FinancialInstitute>; id: string | undefined }>()
);

export const SAVE_ACCOUNT_MANAGER = createAction(
  "[Management] Save Account Manager",
  props<{ accountManager: Partial<AccountManager>; id: string | undefined }>()
);

export const SAVE_INTERMEDIARY = createAction(
  "[Management] Save Intermediary",
  props<{ intermediary: Partial<Intermediary>; id: string | undefined }>()
);

export const SAVE_EMAIL = createAction(
  "[Management] Save Email",
  props<{
    email: Partial<Email>;
    sorting?: TableSorting;
  }>()
);

export const UPLOAD_DOCUMENT = createAction(
  "[Management] Upload Document",
  props<{ id: string; document: File }>()
);

export const DOWNLOAD_DOCUMENT = createAction(
  "[Management] Download Document",
  props<{ id: string; documentName: string }>()
);

export const LOAD_EMAIL = createAction(
  "[Management] Load Email",
  props<{ id: string }>()
);

export const SET_EMAIL = createAction(
  "[Management] Set Email",
  props<{ email: Email }>()
);

export const LOAD_ETF_LIST = createAction(
  "[Management] Load ETF List",
  props<{
    searchTerm?: string;
    sorting?: TableSorting;
  }>()
);

export const SET_ETF_LIST = createAction(
  "[Management] Set ETF List",
  props<{ etfList: Array<Etf> }>()
);

export const SAVE_ETF = createAction(
  "[Management] Save ETF",
  props<{ etf: Partial<Etf>; id: string | undefined }>()
);

export const DELETE_ETF = createAction(
  "[Management] Delete ETF",
  props<{ id: string }>()
);

export const CHECK_FUND_ISIN = createAction(
  "[Management] Check Fund ISIN",
  props<{ isin: string }>()
);

export const SET_FUND_ISIN_VALIDITY = createAction(
  "[Management] Set Fund ISIN Validity",
  props<{ valid: boolean }>()
);

export const CHECK_FOR_EXISTING_EMAIL = createAction(
  "[Management] Check For Existing Email",
  props<{ email: string }>()
);

export const SET_EMAIL_VALIDITY = createAction(
  "[Management] Set Email Validity",
  props<{ emailExists: boolean }>()
);

export const CHECK_FOR_EXISTING_USERNAME = createAction(
  "[Management] Check For Existing Username",
  props<{ username: string }>()
);

export const SET_USERNAME_VALIDITY = createAction(
  "[Management] Set Username Validity",
  props<{ usernameExists: boolean }>()
);

export const SET_MANAGEMENT_PAGINATION = createAction(
  "[Management] Set Management Pagination",
  props<{ pagination: Partial<TPagination> | undefined }>()
);

export const SET_DOCUMENTS_PAGINATION = createAction(
  "[Management] Set Documents Pagination",
  props<{ pagination: Partial<TPagination> | undefined }>()
);

export const SET_MANAGEMENT_LIST_LENGTH = createAction(
  "[Management] Set Management List Length",
  props<{ length: number }>()
);

export const SET_DOCUMENTS_LIST_LENGTH = createAction(
  "[Management] Set Documents List Length",
  props<{ length: number }>()
);

export const SET_FINANCIAL_INSTITUTIONS_LOADING = createAction(
  "[Management] Set Financial Institutions Loading",
  props<{ loading: boolean }>()
);

export const VALIDATE_IBAN = createAction(
  "[Management] Validate Iban",
  props<{ iban: string }>()
);
